import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
	LocalizationProvider,
	MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/de";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useField } from "formik";

dayjs.extend(utc);
dayjs.extend(timezone);

type Props = {
	label: string;
	id: string;
	name: string;
	[key: string]: unknown;
};

const CustomMobileViewDateTimePicker = ({ label, id, ...props }: Props) => {
	const [field, meta, helpers] = useField<string>(props.name);
	const { timezone: timezoneName } = useTime();

	const hasError = Boolean(meta.touched && meta.error);

	const handleDateChange = (date: Dayjs | null) => {
		const formattedDate = date ? dayjs(date).tz(timezoneName).format() : "";
		void helpers.setValue(formattedDate);
	};

	const parsedDate =
		typeof field.value === "string" && field.value
			? dayjs(field.value).tz(timezoneName)
			: null;
	const isValidDate = parsedDate?.isValid() ? parsedDate : null;

	return (
		<div className="input-container">
			<Typography
				component="label"
				htmlFor={id}
				variant="body2"
				sx={{
					color: "text.secondary",
					mb: 0.5,
					display: "block",
					cursor: "pointer",
				}}
			>
				<Translate String={props.name}>{label}</Translate>
			</Typography>
			<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
				<MobileDateTimePicker
					value={isValidDate}
					onChange={handleDateChange}
					renderInput={(params: React.ComponentProps<typeof TextField>) => (
						<TextField
							{...params}
							id={id}
							error={hasError}
							helperText={hasError ? meta.error : ""}
							fullWidth
							size="small"
							sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
						/>
					)}
					{...props}
				/>
			</LocalizationProvider>
		</div>
	);
};

export default CustomMobileViewDateTimePicker;
